import React from "react";

import { Button } from "~/components/button";
import { useGridLayoutContext } from "~/components/grid-layout/use-grid-layout-context";
import { IconQuestionCircleOutlined } from "~/components/icon";
import { SkeletonBox } from "~/components/skeleton/box";
import { SkeletonText } from "~/components/skeleton/text";
import { Tooltip } from "~/components/tooltip";
import { TooltipContent } from "~/components/tooltip/content";
import { TooltipTrigger } from "~/components/tooltip/trigger";
import { Truncated } from "~/components/truncated";
import type { MetricQuery } from "~/models/metric-query";
import { cx } from "~/tools/class-variance";
import { drop } from "~/tools/drop";
import { useTemplateVariables } from "~/tools/template-variables/use-template-variables";
import { useMemo } from "~/tools/use-memo";

import { SpecificRange, type TimeRange } from "../../app/state/view/timeline/TimeRange";
import type { DashboardWidgetSpec } from "../../common/dashboard-spec";
import { Widget, WidgetBody, WidgetHeader } from "../_widget";
import { LoadingSpinner } from "../react/loading-spinner/LoadingSpinner";

import { DashboardWidgetActions, type DashboardWidgetActionsProps } from "./_dashboard-widget-actions";
import { DashboardWidgetLinks } from "./_dashboard-widget-links";

const LazyDashboardGaugeWidget = React.lazy(async () => {
	const { DashboardGaugeWidget } = await import("./_dashboard-gauge-widget");
	return { default: DashboardGaugeWidget };
});

const LazyDashboardStatWidget = React.lazy(async () => {
	const { DashboardStatWidget } = await import("./_dashboard-stat-widget");
	return { default: DashboardStatWidget };
});

const LazyDashboardTimeSeriesWidget = React.lazy(async () => {
	const { DashboardTimeSeriesWidget } = await import("./_dashboard-time-series-widget");
	return { default: DashboardTimeSeriesWidget };
});

const LazyDashboardMarkdownWidget = React.lazy(async () => {
	const { DashboardMarkdownWidget } = await import("./_dashboard-markdown-widget");
	return { default: DashboardMarkdownWidget };
});

export interface DashboardWidgetProps extends DashboardWidgetActionsProps {
	className?: string;
	style?: React.CSSProperties;
	identity: string;
	spec: DashboardWidgetSpec;
	timeRange: TimeRange;
	onTimeRangeChange?: (timeRange: TimeRange) => void;
	children?: React.ReactNode;
}

export const DashboardWidget = React.forwardRef<HTMLDivElement, DashboardWidgetProps>(
	(
		{ className, identity, spec, timeRange, onTimeRangeChange, onDelete, onEdit, onEnlarge, children, ...props },
		ref,
	) => {
		const variables = useTemplateVariables();

		const gridLayoutContext = useGridLayoutContext();
		const primaryLink = spec.plugin.links.at(0);

		const interpolatedName = spec.name.interpolate(variables.values);
		const interpolatedDescription = spec.description.interpolate(variables.values);

		const metricQueries = useMemo(() => {
			const queries = "queries" in spec.plugin ? spec.plugin.queries : [];

			return queries.map(
				({ query, alias, minStep }): MetricQuery => ({
					promql: query.interpolate(variables.values) ?? "",
					label: alias?.addVariables(variables.values),
					step: minStep?.interpolate(variables.values),
				}),
			);
		}, [spec.plugin, variables]);

		return (
			<Widget card ref={ref} {...props} className={cx(className, "group/dashboard-widget")}>
				<WidgetHeader
					className={cx(
						"grid-cols-[1fr_auto] justify-items-stretch gap-x-6",
						onEdit && "cursor-grab active:cursor-grabbing",
						gridLayoutContext?.dragHandleClassName,
					)}
					description={
						primaryLink != null && (
							<DashboardWidgetLinks otherLinks={drop(spec.plugin.links, 1)} primaryLink={primaryLink} />
						)
					}
					title={
						<SkeletonText className="flex flex-row items-center" randomize when={interpolatedName}>
							{(name) => <Truncated>{name}</Truncated>}

							{interpolatedDescription && (
								<Tooltip>
									<TooltipTrigger>
										<Button className={cx("ml-1", gridLayoutContext?.dragCancelClassName)} icon size="xs">
											<IconQuestionCircleOutlined className="text-base/none text-gray-600" />
										</Button>
									</TooltipTrigger>

									<TooltipContent>{interpolatedDescription}</TooltipContent>
								</Tooltip>
							)}

							<LoadingSpinner
								// show the spinner with a delay to avoid flickering when loading is very fast
								className="ml-3 items-start opacity-0 transition-opacity duration-200 group-has-data-[state=fetching]/dashboard-widget:opacity-100 group-has-data-[state=fetching]/dashboard-widget:delay-100"
								size="small"
							/>
						</SkeletonText>
					}
				>
					<DashboardWidgetActions
						onDelete={onDelete}
						onEdit={onEdit}
						onEnlarge={spec.plugin.kind === "Markdown" ? undefined : onEnlarge}
					/>
				</WidgetHeader>

				<WidgetBody className="min-h-0 flex-1">
					<React.Suspense fallback={<SkeletonBox className="size-full" />}>
						{spec.plugin.kind === "TimeSeriesChart" && (
							<LazyDashboardTimeSeriesWidget
								display="line"
								identity={identity}
								metricQueries={metricQueries}
								onBrush={
									onTimeRangeChange ? (start, end) => onTimeRangeChange(new SpecificRange(start, end)) : undefined
								}
								spec={spec.plugin.spec}
								timeRange={timeRange}
								title={interpolatedName}
							/>
						)}
						{spec.plugin.kind === "BarChart" && (
							<LazyDashboardTimeSeriesWidget
								display="bar"
								identity={identity}
								metricQueries={metricQueries}
								onBrush={
									onTimeRangeChange ? (start, end) => onTimeRangeChange(new SpecificRange(start, end)) : undefined
								}
								spec={spec.plugin.spec}
								timeRange={timeRange}
								title={interpolatedName}
							/>
						)}
						{spec.plugin.kind === "GaugeChart" && (
							<LazyDashboardGaugeWidget
								identity={identity}
								metricQueries={metricQueries}
								spec={spec.plugin.spec}
								timeRange={timeRange}
							/>
						)}
						{spec.plugin.kind === "StatChart" && (
							<LazyDashboardStatWidget
								identity={identity}
								metricQueries={metricQueries}
								spec={spec.plugin.spec}
								timeRange={timeRange}
							/>
						)}
						{spec.plugin.kind === "Markdown" && <LazyDashboardMarkdownWidget spec={spec.plugin.spec} />}
					</React.Suspense>
				</WidgetBody>

				{/* The dashboard container mounts a resize handle as a child */}
				{children}
			</Widget>
		);
	},
);
